"use client";

import { Children, type PropsWithChildren, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { BREAKPOINTS } from "@/globals";
import { useWindowSize } from "@/hooks/use-window-size.hook";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";

type CarouselModuleProps = {
    entryId: string;

    itemsPerSlide: number;
};

export const CarouselModuleView = ({
    entryId,
    children,
    itemsPerSlide,
}: PropsWithChildren<CarouselModuleProps>) => {
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [visibleItemCount, setVisibleItemCount] = useState(0);
    const windowSize = useWindowSize();
    const countDots = Children.count(children) - visibleItemCount + 1;
    const centered = Children.count(children) < visibleItemCount;

    const handleScroll = () => {
        if (sliderRef.current) {
            const itemWidth =
                sliderRef.current.offsetWidth /
                (100 /
                    parseFloat(getComputedStyle(sliderRef.current.firstElementChild!).flexBasis));
            const index = Math.round(sliderRef.current.scrollLeft / itemWidth);
            setActiveIndex(index);
        }
    };

    const handleDotClick = (index: number) => {
        if (sliderRef.current) {
            const offset = Math.max(Math.min(index, countDots - 1), 0);
            const itemWidth =
                sliderRef.current.offsetWidth /
                (100 /
                    parseFloat(getComputedStyle(sliderRef.current.firstElementChild!).flexBasis));
            sliderRef.current.scrollTo({
                left: offset * itemWidth,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        const ref = sliderRef.current;

        if (ref) {
            if (ref.firstElementChild) {
                setVisibleItemCount(
                    Math.floor(100 / parseFloat(getComputedStyle(ref.firstElementChild).flexBasis)),
                );
            }
        }
    }, [windowSize]);

    useEffect(() => {
        const ref = sliderRef.current;

        if (ref) {
            ref.addEventListener("scroll", handleScroll);
            return () => {
                ref.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);

    return (
        <div className="relative">
            <style>{`
                .style-${entryId}-container {
                    display: flex;
                    overflow-x: auto;
                    scroll-snap-type: x mandatory;
                    -webkit-overflow-scrolling: touch;
                    width: calc(100% + 2rem);
                    margin:0 -1rem;
                    justify-content:${centered ? "center" : "flex-start"};
                   
                }
                .style-${entryId}-item {
                    scroll-snap-align: start;
                    flex: 0 0 100%;
                    box-sizing: border-box;
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                   
                    @media (min-width: ${BREAKPOINTS.sm}px) {
                        flex: 0 0 ${Math.max(50, 100 / itemsPerSlide)}%;
                    }

                    @media (min-width: ${BREAKPOINTS.md}px) {
                        flex: 0 0 ${Math.max(100 / 3, 100 / itemsPerSlide)}%;
                    }

                    @media (min-width: ${BREAKPOINTS.lg}px) {
                        flex: 0 0 ${Math.max(25, 100 / itemsPerSlide)}%;
                    }
                   
                }
               
                .style-${entryId}-dot {
                    height: 16px;
                    width: 16px;
                    background-color: #bbb;
                    border-radius: 50%;
                    display: inline-block;
                    transition: background-color 0.3s ease;
                }
                .style-${entryId}-dot.active {
                    background-color: #717171;
                   
                }
                .style-${entryId}-container::-webkit-scrollbar {
                    display: none; /* Optional: hides the scrollbar for a cleaner look */
                }
            `}</style>
            <div
                className={`style-${entryId}-container ${visibleItemCount ? "opacity-100" : "opacity-0"}`}
                ref={sliderRef}
                onScroll={handleScroll}
            >
                {children}
            </div>
            {countDots > 1 && (
                <div className={twMerge(WRAPPER_STYLES.HCenterCenter(), "h-16")}>
                    <button
                        onClick={() => handleDotClick(activeIndex - 1)}
                        className={twMerge(BUTTON_STYLES.plain(), TYPO_STYLES.icon(), "text-3xl")}
                    >
                        {String.fromCharCode(Number("0xe917"))}
                    </button>
                    {Array.from({ length: countDots }).map((_, index) => (
                        <button
                            className="py-4 px-2 pt-6"
                            onClick={() => handleDotClick(index)}
                            key={index}
                        >
                            <span
                                className={`style-${entryId}-dot ${activeIndex === index ? "active" : ""}`}
                            />
                        </button>
                    ))}
                    <button
                        className={twMerge(BUTTON_STYLES.plain(), TYPO_STYLES.icon(), "text-3xl ")}
                        onClick={() => handleDotClick(activeIndex + 1)}
                    >
                        {String.fromCharCode(Number("0xe918"))}
                    </button>
                </div>
            )}
        </div>
    );
};

type CarouselItemProps = {
    entryId: string;
};

export const CarouselItemView = ({ children, entryId }: PropsWithChildren<CarouselItemProps>) => (
    <div className={`style-${entryId}-item px-4`}>{children}</div>
);
